import { Stack } from '@mui/material';
import { isEmpty } from 'lodash';
import { ReactNode, useLayoutEffect, useState } from 'react';

import { DocumentCard } from '@/components/DocumentCard';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { LoadingAdornment } from '@/components/form/baseInputs/SelectInput/LoadingAdornment';
import { TypeaheadSelectInput } from '@/components/form/baseInputs/TypeaheadSelectInput/TypeaheadSelectInput';
import { useFormFieldsDisabled } from '@/components/form/context/formFieldsDisabled.context';
import { Trash01Icon } from '@/components/icons/Trash01Icon';
import { PasswordProtectedFilesList } from '@/modules/documents/components/PasswordProtectedFilesList';
import { DOCUMENT_TYPE_OPTIONS } from '@/modules/documents/documents.constants';
import { useMultiDocumentUploaderContext } from '@/modules/documents/MultiDocumentUploader/context/multiDocumentUploader.context';
import {
  FileUploaderV2,
  FileUploaderV2Props,
} from '@/modules/files/FileUploaderV2';
import { UploadFileV2Mutation } from '@/modules/files/graphql/FileUploaderV2.generated';
import { DocumentType, FileContext } from '@/types/schema';

const UPLOADED_FILES_SCROLL_TARGET = 'uploaded-files-scroll-target';

interface MultiDocumentUploaderWithListV2Props {
  householdId: string;
  allowSelectExistingDocuments?: boolean;
  excludedExistingDocumentTypes?: DocumentType[];
  variant?: FileUploaderV2Props<unknown>['variant'];
  appearance?: FileUploaderV2Props<unknown>['appearance'];
  /**
   * Optional content rendered between the dropzone and the uploaded-files list
   * (e.g. the SmartReview onboarding recommendations panel). Defaults to
   * nothing, so existing callers render the dropzone directly above the list.
   */
  belowUploaderContent?: ReactNode;
  /** Width (px) of each row's document-type select. Default: 160. */
  documentTypeSelectWidth?: number;
  /** MUI spacing between uploaded-file rows. Default: 2 (16px). */
  uploadedListSpacing?: number;
  /** Whether uploads scroll the uploaded-files list into view. Default: true. */
  autoScrollToUploadedFiles?: boolean;
  /** Optional styles applied to the file-uploader dropzone. */
  uploaderSx?: FileUploaderV2Props<unknown>['sx'];
}

export function MultiDocumentUploaderWithListV2({
  householdId,
  allowSelectExistingDocuments = false,
  excludedExistingDocumentTypes,
  variant = 'compact',
  appearance = 'default',
  belowUploaderContent,
  documentTypeSelectWidth = 160,
  uploadedListSpacing = 2,
  autoScrollToUploadedFiles = true,
  uploaderSx,
}: MultiDocumentUploaderWithListV2Props) {
  const { disabled: isDisabled } = useFormFieldsDisabled();
  const [protectedFiles, setProtectedFiles] = useState<
    { name: string; id: string }[]
  >([]);

  const {
    uploadedFiles,
    handleUploadFilesStarted,
    handleUploadFilesComplete,
    handleDeleteFile,
    setDocumentTypeOfUploadedFile,
    setSelectedExistingDocuments,
    selectedExistingDocuments,
    fileIDTypeMap,
    uploaderErrorMessage,
    inferringDocumentTypes,
  } = useMultiDocumentUploaderContext();

  useLayoutEffect(() => {
    if (!autoScrollToUploadedFiles || isEmpty(uploadedFiles)) {
      return;
    }
    document.getElementById(UPLOADED_FILES_SCROLL_TARGET)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [autoScrollToUploadedFiles, uploadedFiles]);

  return (
    <Stack gap={2}>
      <FileUploaderV2<UploadFileV2Mutation>
        sx={uploaderSx}
        variant={variant}
        appearance={appearance}
        householdId={householdId}
        onUploadStarted={handleUploadFilesStarted}
        onUploadComplete={(uploadResults, passwordProtectedFiles) => {
          handleUploadFilesComplete(uploadResults);

          if (passwordProtectedFiles && passwordProtectedFiles.length > 0) {
            const newProtectedFiles = passwordProtectedFiles.map((file) => ({
              name: file.name,
              id: `${file.name}-${Date.now()}`,
            }));
            setProtectedFiles((prev) => [...prev, ...newProtectedFiles]);
          }
        }}
        errorMessage={uploaderErrorMessage}
        showExistingDocuments={allowSelectExistingDocuments}
        excludedExistingDocumentTypes={excludedExistingDocumentTypes}
        selectedExistingDocumentIds={selectedExistingDocuments.map((d) => d.id)}
        onExistingSelected={(documents) => {
          setSelectedExistingDocuments(
            documents.map((doc) => ({
              id: doc.id,
              displayName: doc.displayName,
            }))
          );
        }}
        allowMultiple
        context={FileContext.Household}
        disabled={isDisabled}
      />
      {belowUploaderContent}
      <Stack spacing={uploadedListSpacing} id={UPLOADED_FILES_SCROLL_TARGET}>
        {/* Display password protected files with error state */}
        <PasswordProtectedFilesList
          protectedFiles={protectedFiles}
          onDeleteFile={(fileId) => {
            setProtectedFiles((prev) =>
              prev.filter((file) => file.id !== fileId)
            );
          }}
        />

        {/* Display regular uploaded files */}
        {uploadedFiles.map((uploadedFile, index) => {
          const { fileId, fileName } = uploadedFile;

          return (
            <DocumentCard
              key={fileId}
              filename={fileName}
              status="complete"
              controls={
                <Stack direction="row" spacing={1} alignItems="center">
                  <TypeaheadSelectInput<DocumentType | ''>
                    required
                    sx={{ width: documentTypeSelectWidth }}
                    name={`document-type-${index}`}
                    label="Specify type"
                    hideLabel
                    value={fileIDTypeMap?.[fileId] || ''}
                    options={DOCUMENT_TYPE_OPTIONS}
                    placeholder="Search document types..."
                    disablePortal={false}
                    dropdownProps={{
                      style: {
                        width: 'min(400px, calc(100vw - 32px))',
                      },
                    }}
                    onChange={(_event, documentType) => {
                      if (documentType) {
                        setDocumentTypeOfUploadedFile(fileId, documentType);
                      }
                    }}
                    startAdornment={
                      inferringDocumentTypes && <LoadingAdornment />
                    }
                    disabled={isDisabled}
                  />
                  <IconButton
                    icon={Trash01Icon}
                    ariaLabel="delete"
                    size="sm"
                    variant="transparent"
                    onClick={() => handleDeleteFile(fileId)}
                    disabled={isDisabled}
                  />
                </Stack>
              }
            />
          );
        })}
      </Stack>
    </Stack>
  );
}
